import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  ArchiveRestore,
  BarChart3,
  EyeOff,
  FileText,
  Globe,
  ListMusic,
  MoreHorizontal,
  Pencil,
  Play,
  Share2,
  Tag,
  Trash2,
} from 'lucide-react';
import { isLyrraOne } from '@/lib/platform';

export type PodcastRowActionsProps = {
  isArchived: boolean;
  isActive: boolean;
  onPreview: () => void;
  onContents: () => void;
  onImportDocument: () => void;
  onShare: () => void;
  onOpenOffer?: () => void;
  onEdit: () => void;
  onToggleActive: () => void;
  onArchive: () => void;
  onRestoreArchive: () => void;
  onDelete: () => void;
  onOpenDashboard?: () => void;
};

type MenuItem = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  className?: string;
  dividerBefore?: boolean;
  disabled?: boolean;
};

export function PodcastRowActions({
  isArchived,
  isActive,
  onPreview,
  onContents,
  onImportDocument,
  onShare,
  onOpenOffer,
  onEdit,
  onToggleActive,
  onArchive,
  onRestoreArchive,
  onDelete,
  onOpenDashboard,
}: PodcastRowActionsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  const updateMenuPos = () => {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPos();
    const onScrollOrResize = () => updateMenuPos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const node = e.target as Node;
      if (buttonRef.current?.contains(node)) return;
      if (menuRef.current?.contains(node)) return;
      setOpen(false);
      setMenuPos(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    setMenuPos(null);
  };

  const run = (fn: () => void) => {
    fn();
    closeMenu();
  };

  const items: MenuItem[] = [
    {
      key: 'preview',
      label: t('podcasts.list.actionPreview'),
      icon: Play,
      onClick: () => run(onPreview),
    },
    {
      key: 'episodes',
      label: t('podcasts.list.actionManageEpisodes'),
      icon: ListMusic,
      onClick: () => run(onContents),
    },
  ];

  if (!isArchived) {
    items.push({
      key: 'import',
      label: t('podcasts.list.actionImportDocument'),
      icon: FileText,
      onClick: () => run(onImportDocument),
    });
  }

  items.push({
    key: 'share',
    label: t('podcasts.actions.share'),
    icon: Share2,
    onClick: () => run(onShare),
    disabled: isArchived,
  });

  if (onOpenDashboard) {
    items.push({
      key: 'dashboard',
      label: t('contentDashboard.open', 'Tableau de bord'),
      icon: BarChart3,
      onClick: () => run(onOpenDashboard),
    });
  }

  if (isLyrraOne() && isActive && !isArchived && onOpenOffer) {
    items.push({
      key: 'offer',
      label: t('lyrraone.store.offerTitle', 'Paramètres de vente'),
      icon: Tag,
      onClick: () => run(onOpenOffer),
    });
  }

  items.push({
    key: 'edit',
    label: t('podcasts.actions.edit'),
    icon: Pencil,
    onClick: () => run(onEdit),
  });

  if (isArchived) {
    items.push({
      key: 'restore',
      label: t('podcasts.list.restoreFromArchive'),
      icon: ArchiveRestore,
      onClick: () => run(onRestoreArchive),
    });
  } else {
    items.push({
      key: 'toggle-active',
      label: isActive ? t('podcasts.list.actionDeactivate') : t('podcasts.list.actionActivate'),
      icon: isActive ? EyeOff : Globe,
      onClick: () => run(onToggleActive),
      className: isActive
        ? 'text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30'
        : 'text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30',
    });
    items.push({
      key: 'archive',
      label: t('podcasts.list.archivePodcast'),
      icon: Archive,
      onClick: () => run(onArchive),
    });
  }

  items.push({
    key: 'delete',
    label: t('podcasts.list.actionDelete'),
    icon: Trash2,
    onClick: () => run(onDelete),
    className: 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30',
    dividerBefore: true,
  });

  const menu =
    open &&
    menuPos &&
    createPortal(
      <ul
        ref={menuRef}
        style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
        className="z-[200] min-w-[200px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        role="menu"
      >
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li
              key={item.key}
              role="none"
              className={item.dividerBefore ? 'border-t border-gray-100 dark:border-gray-800' : undefined}
            >
              <button
                type="button"
                onClick={item.onClick}
                disabled={item.disabled}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40 dark:text-gray-200 dark:hover:bg-gray-800 ${item.className ?? ''}`}
                role="menuitem"
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="text-left">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>,
      document.body,
    );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((wasOpen) => {
            if (wasOpen) {
              setMenuPos(null);
              return false;
            }
            const el = buttonRef.current;
            if (el) {
              const r = el.getBoundingClientRect();
              setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
            }
            return true;
          });
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        aria-label={t('common.actions', 'Actions')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {menu}
    </>
  );
}
